import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useMultipleRealtimeSubscriptions } from '../../hooks/useRealtimeSubscription';
import AdminSidebar from '../../components/ui/AdminSidebar';
import MetricsCard from './components/MetricsCard';
import TopDealershipsPanel from './components/TopDealershipsPanel';


import QuickActionsPanel from './components/QuickActionsPanel';
import ScheduledReportsModal from './components/ScheduledReportsModal';
import CustomWidgetsPanel from './components/CustomWidgetsPanel';
import DrillDownAnalyticsModal from './components/DrillDownAnalyticsModal';
import EmailTemplateManager from './components/EmailTemplateManager';
import GmailSMTPTestPanel from './components/GmailSMTPTestPanel';
import AuditLogViewer from './components/AuditLogViewer';
import SecurityAlertsPanel from './components/SecurityAlertsPanel';
import ErrorLogViewer from './components/ErrorLogViewer';

import { logSupabaseError } from '../../services/errorLogService';
import Button from '../../components/ui/Button';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [dealershipFilter, setDealershipFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showWidgetsPanel, setShowWidgetsPanel] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [showSMTPTest, setShowSMTPTest] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [showSecurityAlerts, setShowSecurityAlerts] = useState(false);
  const [showErrorLogs, setShowErrorLogs] = useState(false);

  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState([]);
  const [topDealerships, setTopDealerships] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Real-time subscriptions for applications, dealerships, and vouchers
  useMultipleRealtimeSubscriptions([
    {
      table: 'applications',
      onInsert: (newApp) => {
        console.log('New application created:', newApp);
        // Refresh dashboard data to show new application
        fetchDashboardData();
      },
      onUpdate: (updatedApp) => {
        console.log('Application updated:', updatedApp);
        // Update specific application in state or refresh
        setApplications(prev => 
          prev?.map(app => app?.id === updatedApp?.id ? {
            id: updatedApp?.id,
            customerName: updatedApp?.customer_name,
            email: updatedApp?.customer_email,
            dealershipName: updatedApp?.dealerships?.name || 'Unknown',
            chassisNumber: updatedApp?.chassis_number || 'N/A',
            status: updatedApp?.application_status,
            submissionDate: new Date(updatedApp.application_date)?.toLocaleDateString('de-DE')
          } : app)
        );
        // Also refresh metrics
        fetchDashboardData();
      },
      onDelete: (deletedApp) => {
        console.log('Application deleted:', deletedApp);
        // Remove from state
        setApplications(prev => prev?.filter(app => app?.id !== deletedApp?.id));
        // Refresh metrics
        fetchDashboardData();
      }
    },
    {
      table: 'dealerships',
      onInsert: (newDealership) => {
        console.log('New dealership created:', newDealership);
        fetchDashboardData();
      },
      onUpdate: (updatedDealership) => {
        console.log('Dealership updated:', updatedDealership);
        fetchDashboardData();
      },
      onDelete: (deletedDealership) => {
        console.log('Dealership deleted:', deletedDealership);
        fetchDashboardData();
      }
    },
    {
      table: 'vouchers',
      onInsert: (newVoucher) => {
        console.log('New voucher created:', newVoucher);
        fetchDashboardData();
      },
      onUpdate: (updatedVoucher) => {
        console.log('Voucher updated:', updatedVoucher);
        fetchDashboardData();
      },
      onDelete: (deletedVoucher) => {
        console.log('Voucher deleted:', deletedVoucher);
        fetchDashboardData();
      }
    }
  ]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all applications with dealership info
      const { data: appsData, error: appsError } = await supabase?.from('applications')?.select(`
          *,
          dealerships (
            id,
            name,
            city
          )
        `)?.order('application_date', { ascending: false });

      if (appsError) {
        // Log error to centralized system
        await logSupabaseError({
          error: appsError,
          query: 'fetchDashboardData - applications',
          table: 'applications',
          operation: 'SELECT',
          componentName: 'AdminDashboard',
          functionName: 'fetchDashboardData'
        });
        throw appsError;
      }

      // Fetch all dealerships
      const { data: dealershipsData, error: dealershipsError } = await supabase?.from('dealerships')?.select('*')?.eq('status', 'active')?.order('name');

      if (dealershipsError) {
        // Log error to centralized system
        await logSupabaseError({
          error: dealershipsError,
          query: 'fetchDashboardData - dealerships',
          table: 'dealerships',
          operation: 'SELECT',
          componentName: 'AdminDashboard',
          functionName: 'fetchDashboardData'
        });
        throw dealershipsError;
      }

      // Calculate current period metrics
      const totalApplications = appsData?.length || 0;
      const approvedCount = appsData?.filter(app => app?.application_status === 'approved')?.length || 0;
      const openCount = appsData?.filter(app => app?.application_status === 'pending')?.length || 0;
      const approvalRate = totalApplications > 0 ? ((approvedCount / totalApplications) * 100)?.toFixed(1) : 0;
      const activeDealerships = dealershipsData?.length || 0;

      // Calculate trends by comparing to last 30 days
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

      const recentApps = appsData?.filter(app => new Date(app?.application_date) >= thirtyDaysAgo) || [];
      const previousApps = appsData?.filter(app => {
        const date = new Date(app?.application_date);
        return date >= sixtyDaysAgo && date < thirtyDaysAgo;
      }) || [];

      const calculateTrend = (current, previous) => {
        if (previous === 0) return { direction: 'up', value: current > 0 ? '+100%' : '0%' };
        const change = ((current - previous) / previous) * 100;
        return {
          direction: change >= 0 ? 'up' : 'down',
          value: `${change >= 0 ? '+' : ''}${change?.toFixed(1)}%`
        };
      };

      const applicationsTrend = calculateTrend(recentApps?.length, previousApps?.length);
      
      const recentApprovedCount = recentApps?.filter(app => app?.application_status === 'approved')?.length || 0;
      const previousApprovedCount = previousApps?.filter(app => app?.application_status === 'approved')?.length || 0;
      const recentApprovalRate = recentApps?.length > 0 ? (recentApprovedCount / recentApps?.length) * 100 : 0;
      const previousApprovalRate = previousApps?.length > 0 ? (previousApprovedCount / previousApps?.length) * 100 : 0;
      const approvalRateTrend = calculateTrend(recentApprovalRate, previousApprovalRate);

      // For dealerships, count how many were created in last 30 days vs previous 30 days
      const recentDealerships = dealershipsData?.filter(d => new Date(d?.created_at) >= thirtyDaysAgo)?.length || 0;
      const previousDealerships = dealershipsData?.filter(d => {
        const date = new Date(d?.created_at);
        return date >= sixtyDaysAgo && date < thirtyDaysAgo;
      })?.length || 0;
      const dealershipsTrend = calculateTrend(recentDealerships, previousDealerships);

      const recentOpenCount = recentApps?.filter(app => app?.application_status === 'pending')?.length || 0;
      const previousOpenCount = previousApps?.filter(app => app?.application_status === 'pending')?.length || 0;
      const openCountTrend = calculateTrend(recentOpenCount, previousOpenCount);

      setMetricsData([
        {
          title: 'Gesamtanträge',
          value: totalApplications?.toString(),
          subtitle: 'Alle Zeiträume',
          icon: 'FileText',
          color: 'primary',
          trend: applicationsTrend?.direction,
          trendValue: applicationsTrend?.value
        },
        {
          title: 'Genehmigungsrate',
          value: `${approvalRate}%`,
          subtitle: 'Durchschnittlich',
          icon: 'CheckCircle2',
          color: 'success',
          trend: approvalRateTrend?.direction,
          trendValue: approvalRateTrend?.value
        },
        {
          title: 'Aktive Partner',
          value: activeDealerships?.toString(),
          subtitle: 'Partnerschaften',
          icon: 'Building2',
          color: 'secondary',
          trend: dealershipsTrend?.direction,
          trendValue: dealershipsTrend?.value
        },
        {
          title: 'Offene Anträge',
          value: openCount?.toString(),
          subtitle: 'Benötigen Bearbeitung',
          icon: 'Clock',
          color: 'warning',
          trend: openCountTrend?.direction,
          trendValue: openCountTrend?.value
        }
      ]);

      // Calculate top dealerships by application count
      const dealershipStats = {};
      appsData?.forEach(app => {
        const dealershipId = app?.dealership_id;
        const dealershipName = app?.dealerships?.name || 'Unknown';
        const dealershipCity = app?.dealerships?.city || '';
        
        if (!dealershipStats?.[dealershipId]) {
          dealershipStats[dealershipId] = {
            id: dealershipId,
            name: dealershipName,
            location: dealershipCity,
            applicationCount: 0,
            trend: 'up'
          };
        }
        dealershipStats[dealershipId].applicationCount += 1;
      });

      const topDealershipsList = Object.values(dealershipStats)?.sort((a, b) => b?.applicationCount - a?.applicationCount)?.slice(0, 10);

      setTopDealerships(topDealershipsList);

      // Format dealerships for filter dropdown
      const dealershipsList = dealershipsData?.map(d => ({
        id: d?.id,
        name: d?.name
      })) || [];
      setDealerships(dealershipsList);

      // Format applications
      const formattedApplications = appsData?.map(app => {
        // For companies, show "Company Name (Contact Person)"
        let displayName = app?.customer_name;
        if (app?.customer_gender === 'company' && app?.company_name) {
          displayName = `${app?.company_name} (${app?.customer_name})`;
        }

        return {
          id: app?.id,
          customerName: displayName,
          customerGender: app?.customer_gender,
          email: app?.customer_email,
          dealershipName: app?.dealerships?.name || 'Unknown',
          chassisNumber: app?.chassis_number || 'N/A',
          status: app?.application_status,
          wantsAnnualRenewal: app?.wants_annual_renewal,
          submissionDate: new Date(app?.application_date)?.toLocaleDateString('de-DE')
        };
      }) || [];
      setApplications(formattedApplications);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setDealershipFilter('all');
    setDateRange({ start: '', end: '' });
    setSearchQuery('');
  };

  const handleStatusUpdate = (applicationId) => {
    console.log('Update status for application:', applicationId);
  };

  const handleBulkProcess = (selectedIds) => {
    console.log('Bulk process applications:', selectedIds);
  };

  const handleViewDetails = (applicationId) => {
    navigate('/application-processing', { state: { applicationId } });
  };

  const handleNavigate = (route) => {
    navigate(route);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Lade Dashboard-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />
      <div className="lg:ml-60 min-h-screen">
        <div className="p-6 lg:p-8">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-muted-foreground">
                  Zentrale Übersicht über THG-Prämienanträge und Partner-Partnerschaften
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSMTPTest(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  title="Gmail SMTP-Verbindung testen"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  SMTP Test
                </button>
                <button
                  onClick={() => setShowEmailTemplates(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                  title="E-Mail-Vorlagen anpassen"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  E-Mail-Vorlagen
                </button>
                <button
                  onClick={() => setShowReportsModal(true)}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Berichte planen
                </button>
                <button
                  onClick={() => setShowWidgetsPanel(true)}
                  className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors flex items-center gap-2"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                  </svg>
                  Widgets
                </button>
                <button
                  onClick={() => setShowAnalyticsModal(true)}
                  className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/90 transition-colors flex items-center gap-2"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Deep Analytics
                </button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="AlertTriangle"
                  onClick={() => setShowErrorLogs(true)}
                >
                  Fehlerprotokoll
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metricsData?.map((metric, index) => (
              <MetricsCard key={index} {...metric} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopDealershipsPanel dealerships={topDealerships} />
            <QuickActionsPanel 
              onScheduleReports={() => setShowReportsModal(true)}
              onCustomizeWidgets={() => setShowWidgetsPanel(true)}
              onDrillDownAnalytics={() => setShowAnalyticsModal(true)}
              onManageEmailTemplates={() => setShowEmailTemplates(true)}
              onTestSMTP={() => setShowSMTPTest(true)}
              onViewAuditLogs={() => setShowAuditLogs(true)}
              onViewSecurityAlerts={() => setShowSecurityAlerts(true)}
            />
          </div>

          {/* Security Alerts Panel */}
          <SecurityAlertsPanel />
        </div>
      </div>

      <EmailTemplateManager 
        isOpen={showEmailTemplates} 
        onClose={() => setShowEmailTemplates(false)} 
      />
      
      <ScheduledReportsModal 
        isOpen={showReportsModal} 
        onClose={() => setShowReportsModal(false)} 
      />
      
      <CustomWidgetsPanel 
        isOpen={showWidgetsPanel} 
        onClose={() => setShowWidgetsPanel(false)} 
      />
      
      <DrillDownAnalyticsModal 
        isOpen={showAnalyticsModal} 
        onClose={() => setShowAnalyticsModal(false)} 
      />
      
      <GmailSMTPTestPanel 
        isOpen={showSMTPTest} 
        onClose={() => setShowSMTPTest(false)} 
      />

      {/* Audit Log Viewer Modal */}
      {showAuditLogs && (
        <AuditLogViewer onClose={() => setShowAuditLogs(false)} />
      )}

      {/* Error Log Viewer Modal */}
      {showErrorLogs && (
        <ErrorLogViewer onClose={() => setShowErrorLogs(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;